import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Package, ArrowRight, Menu, X, Phone, Mail, MapPinned } from 'lucide-react';
import { PILLAR_NAV, PRODUCT_PILLARS } from '../data/productCatalog';
import { CONTACT, BROCHURE_PDF } from '../data/siteContent';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [showProductsDropdown, setShowProductsDropdown] = useState(false);
  const [showAboutDropdown, setShowAboutDropdown] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const [hoveredPillarSlug, setHoveredPillarSlug] = useState(PILLAR_NAV[0]?.slug ?? '');

  const activePillar = useMemo(
    () => PRODUCT_PILLARS.find((p) => p.slug === hoveredPillarSlug) || PRODUCT_PILLARS[0] || null,
    [hoveredPillarSlug]
  );

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (showProductsDropdown && PILLAR_NAV[0]?.slug) {
      setHoveredPillarSlug(PILLAR_NAV[0].slug);
    }
  }, [showProductsDropdown]);

  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const closeAllMenus = () => {
    setMobileMenuOpen(false);
    setShowProductsDropdown(false);
    setShowAboutDropdown(false);
    setMobileAboutOpen(false);
  };

  const navLink = 'text-neutral-600 font-semibold hover:text-brand-600 px-2 py-2 text-sm transition-all hover:-translate-y-0.5 whitespace-nowrap';

  return (
    <nav
      className={`fixed w-full top-0 left-0 z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-white/90 backdrop-blur-md border-b border-neutral-200/50 shadow-lg h-20'
          : 'bg-white border-b border-neutral-200 h-24'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full relative z-[100] bg-white/95 backdrop-blur-sm">
        <div className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center h-full gap-2 lg:gap-4">
          <Link to="/" className="flex items-center gap-3 min-w-0 z-10" onClick={closeAllMenus}>
            <img src="/images/logo.png" alt="Sai Vikas Coating Logo" className="h-16 w-auto object-contain sm:h-[4.5rem]" />
            <div className="hidden sm:flex flex-col">
              <span className="font-heading font-bold text-xl text-neutral-900 leading-none">Sai Vikas</span>
              <span className="font-body text-xs text-neutral-500 uppercase tracking-wider">Coating</span>
            </div>
          </Link>

          {/* Desktop links — visible from md up */}
          <div className="hidden md:flex md:flex-nowrap md:items-center md:justify-center min-w-0 relative z-20">
            <Link to="/" className="text-neutral-900 font-semibold hover:text-brand-600 px-2 py-2 text-sm transition-all whitespace-nowrap">
              Home
            </Link>

            <div
              className="relative"
              onMouseEnter={() => {
                setShowAboutDropdown(true);
                setShowProductsDropdown(false);
              }}
              onMouseLeave={() => setShowAboutDropdown(false)}
            >
              <button
                type="button"
                className={`flex items-center gap-1 font-semibold px-2 py-2 text-sm transition-all whitespace-nowrap ${
                  showAboutDropdown ? 'text-brand-600' : 'text-neutral-600 hover:text-brand-600'
                }`}
                aria-expanded={showAboutDropdown}
                aria-haspopup="true"
              >
                About
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 shrink-0 ${showAboutDropdown ? 'rotate-180' : ''}`}
                />
              </button>
              <div
                className={`absolute left-0 top-full z-[120] min-w-[240px] pt-2 transition-all duration-200 ${
                  showAboutDropdown ? 'visible opacity-100' : 'invisible opacity-0 pointer-events-none'
                }`}
              >
                <div className="overflow-hidden rounded-xl border border-neutral-200 bg-white py-2 shadow-xl">
                  <Link
                    to="/about"
                    className="block px-4 py-2.5 text-sm font-semibold text-neutral-800 hover:bg-brand-50 hover:text-brand-700"
                    onClick={closeAllMenus}
                  >
                    About us
                  </Link>
                  <Link
                    to="/certifications"
                    className="block px-4 py-2.5 text-sm font-semibold text-neutral-800 hover:bg-brand-50 hover:text-brand-700"
                    onClick={closeAllMenus}
                  >
                    Certifications &amp; Awards
                  </Link>
                </div>
              </div>
            </div>

            <Link to="/services" className={navLink}>
              Services
            </Link>

            <div
              className="relative flex items-center h-full"
              onMouseEnter={() => {
                setShowProductsDropdown(true);
                setShowAboutDropdown(false);
              }}
            >
              <button
                type="button"
                className={`flex items-center gap-1 font-semibold px-2 py-2 text-sm transition-all whitespace-nowrap ${
                  showProductsDropdown ? 'text-brand-600' : 'text-neutral-600 hover:text-brand-600'
                }`}
              >
                Products
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-300 shrink-0 ${showProductsDropdown ? 'rotate-180' : ''}`}
                />
              </button>
            </div>

            <Link to="/industries" className={navLink}>
              Industries
            </Link>
            <Link to="/clients" className={navLink}>
              Clients
            </Link>
            <Link to="/gallery" className={navLink}>
              Gallery
            </Link>
          </div>

          <div className="flex items-center justify-end gap-2 sm:gap-3 z-10">
            <a
              href={BROCHURE_PDF}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden lg:inline-flex items-center justify-center px-3 py-2 text-sm font-semibold text-neutral-600 hover:text-brand-600 whitespace-nowrap"
            >
              Brochure
            </a>
            <Link
              to="/contact"
              className="hidden md:inline-flex items-center justify-center px-4 sm:px-5 py-2.5 border border-transparent text-sm font-medium rounded-small text-white bg-brand-900 hover:bg-brand-800 shadow-custom transition-all whitespace-nowrap"
            >
              Contact
            </Link>
            <div className="md:hidden flex items-center">
              <button
                type="button"
                onClick={() => setMobileMenuOpen((open) => !open)}
                className="text-neutral-600 hover:text-brand-600 p-2 transition-colors"
                aria-label="Toggle menu"
                aria-expanded={mobileMenuOpen}
              >
                {mobileMenuOpen ? <X className="w-7 h-7" /> : <Menu className="w-7 h-7" />}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Products mega menu */}
      <div
        className={`absolute top-full left-0 w-full bg-white/95 backdrop-blur-xl border-b border-neutral-200 shadow-2xl transition-all duration-300 origin-top overflow-hidden z-[110] ${
          showProductsDropdown ? 'opacity-100 scale-y-100' : 'opacity-0 scale-y-0 pointer-events-none'
        }`}
        onMouseLeave={() => setShowProductsDropdown(false)}
      >
        <div className="max-w-7xl mx-auto px-8 py-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-5 border-r border-neutral-100 pr-10">
            <h3 className="text-xs font-bold text-brand-600 uppercase tracking-widest mb-6 flex items-center gap-2">
              <Package className="w-4 h-4" />
              Product categories
            </h3>
            <div className="space-y-1">
              {PILLAR_NAV.map((p) => (
                <Link
                  key={p.slug}
                  to={`/products/${p.slug}`}
                  className={`flex items-center justify-between group p-3 rounded-lg transition-all ${
                    hoveredPillarSlug === p.slug ? 'bg-brand-50 ring-1 ring-brand-100' : 'hover:bg-neutral-50'
                  }`}
                  onMouseEnter={() => setHoveredPillarSlug(p.slug)}
                  onFocus={() => setHoveredPillarSlug(p.slug)}
                  onClick={closeAllMenus}
                >
                  <span
                    className={`font-semibold text-sm ${
                      hoveredPillarSlug === p.slug ? 'text-brand-800' : 'text-neutral-800 group-hover:text-brand-600'
                    }`}
                  >
                    {p.title}
                  </span>
                  <ArrowRight className="w-4 h-4 text-neutral-300 group-hover:text-brand-600 group-hover:translate-x-1 transition-all" />
                </Link>
              ))}
              <Link
                to="/products"
                className="flex items-center gap-2 p-3 mt-4 text-brand-600 font-bold text-sm hover:underline"
                onClick={closeAllMenus}
              >
                All products overview <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          <div className="col-span-12 md:col-span-7 flex min-h-[280px] flex-col">
            {activePillar ? (
              <>
                <div className="mb-4">
                  <h4 className="font-heading text-lg font-bold text-neutral-900">{activePillar.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-neutral-600">{activePillar.overview.what}</p>
                  <p className="mt-3 flex items-start gap-2 text-xs leading-relaxed text-neutral-500">
                    <MapPinned className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-500" strokeWidth={2} />
                    <span>{activePillar.overview.whereUsed}</span>
                  </p>
                </div>
                <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-brand-600">Systems in this category</p>
                <ul className="max-h-[220px] space-y-1 overflow-y-auto pr-1 [scrollbar-width:thin]">
                  {activePillar.systems.map((sys) => (
                    <li key={sys.id}>
                      <Link
                        to={`/products/${activePillar.slug}#${sys.id}`}
                        className="group flex items-start gap-2 rounded-lg px-2 py-2 text-sm text-neutral-800 transition hover:bg-neutral-50"
                        onClick={closeAllMenus}
                      >
                        <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-400 transition group-hover:translate-x-0.5 group-hover:text-brand-600" />
                        <span className="font-medium leading-snug">{sys.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto border-t border-neutral-100 pt-4">
                  <Link
                    to={`/products/${activePillar.slug}`}
                    className="inline-flex items-center gap-2 font-semibold text-brand-800 text-sm hover:underline"
                    onClick={closeAllMenus}
                  >
                    Open full {activePillar.title} page <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </>
            ) : (
              <p className="text-sm text-neutral-500">Hover a category to see systems.</p>
            )}
          </div>
        </div>
      </div>

      {/* Mobile drawer — same breakpoint as hamburger (md:hidden) */}
      <div
        className={`fixed inset-0 z-[90] md:hidden ${mobileMenuOpen ? 'pointer-events-auto' : 'pointer-events-none'}`}
        aria-hidden={!mobileMenuOpen}
      >
        <button
          type="button"
          className={`absolute inset-0 bg-black/40 transition-opacity duration-300 ${
            mobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          aria-label="Close menu"
          onClick={closeAllMenus}
        />
        <div
          className={`absolute left-0 right-0 bottom-0 bg-white shadow-2xl transition-transform duration-300 ease-out ${
            scrolled ? 'top-20' : 'top-24'
          } ${mobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}
        >
          <div className="flex flex-col h-full">
            <div className="flex-grow overflow-y-auto py-6 px-6 space-y-6">
              <Link to="/" onClick={closeAllMenus} className="block text-xl font-bold text-neutral-900">
                Home
              </Link>

              <div>
                <button
                  type="button"
                  onClick={() => setMobileAboutOpen((open) => !open)}
                  className="flex w-full items-center justify-between text-xl font-bold text-neutral-900"
                >
                  About
                  <ChevronDown className={`h-5 w-5 transition-transform ${mobileAboutOpen ? 'rotate-180' : ''}`} />
                </button>
                {mobileAboutOpen && (
                  <div className="mt-3 space-y-2 pl-2">
                    <Link
                      to="/about"
                      onClick={closeAllMenus}
                      className="block rounded-xl bg-neutral-50 p-3 text-sm font-semibold text-neutral-800"
                    >
                      About us
                    </Link>
                    <Link
                      to="/certifications"
                      onClick={closeAllMenus}
                      className="block rounded-xl bg-neutral-50 p-3 text-sm font-semibold text-neutral-800"
                    >
                      Certifications &amp; Awards
                    </Link>
                  </div>
                )}
              </div>

              <Link to="/services" onClick={closeAllMenus} className="block text-xl font-bold text-neutral-900">
                Services
              </Link>

              <div>
                <span className="block text-xs font-black text-brand-600 uppercase tracking-widest mb-2">Products</span>
                <div className="space-y-2">
                  {PILLAR_NAV.map((p) => (
                    <Link
                      key={p.slug}
                      to={`/products/${p.slug}`}
                      onClick={closeAllMenus}
                      className="flex items-center justify-between p-3 rounded-xl bg-neutral-50 text-neutral-800 font-semibold text-sm"
                    >
                      {p.title}
                      <ArrowRight className="w-4 h-4 text-brand-600" />
                    </Link>
                  ))}
                </div>
              </div>

              <Link to="/industries" onClick={closeAllMenus} className="block text-xl font-bold text-neutral-900">
                Industries
              </Link>
              <Link to="/clients" onClick={closeAllMenus} className="block text-xl font-bold text-neutral-900">
                Clients
              </Link>
              <Link to="/gallery" onClick={closeAllMenus} className="block text-xl font-bold text-neutral-900">
                Gallery
              </Link>
              <a
                href={BROCHURE_PDF}
                target="_blank"
                rel="noopener noreferrer"
                onClick={closeAllMenus}
                className="block text-xl font-bold text-neutral-900"
              >
                Brochure
              </a>
              <Link to="/contact" onClick={closeAllMenus} className="block text-xl font-bold text-neutral-900">
                Contact
              </Link>

              <div className="pt-6 border-t space-y-4 text-neutral-500">
                <div className="flex items-center gap-3">
                  <Phone className="w-5 h-5 text-brand-600" />
                  <span className="font-medium text-sm">{CONTACT.phones[0]}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Mail className="w-5 h-5 text-brand-600" />
                  <span className="font-medium text-sm">{CONTACT.emailPrimary}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
